import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function Modal({
  isOpen,
  onClose,
  title,
  children
}: ModalProps) {
  // Handle ESC key press
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  return <AnimatePresence data-id="element-38">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-id="element-39">
            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} className="bg-[#111827] border border-[#c9a84c]/20 rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl relative" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-40">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-[#c9a84c]/10" data-id="element-41">
                <h2 id="modal-title" className="text-2xl font-bold text-[#f5f5f5]" data-id="element-42">
                  {title}
                </h2>
                <button onClick={onClose} className="text-[#9ca3af] hover:text-[#f5f5f5] transition-colors p-1 rounded-full hover:bg-white/5" aria-label="Cerrar" data-id="element-43">
                  <XIcon className="w-6 h-6" data-id="element-44" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto text-[#9ca3af] space-y-4 text-sm leading-relaxed" data-id="element-45">
                {children}
              </div>
            </motion.div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  return <footer className="bg-[#05070d] py-12 border-t border-[#c9a84c]/10" data-id="element-46">
      <div className="max-w-5xl mx-auto px-4 flex flex-col items-center text-center" data-id="element-47">
        <p className="text-[#9ca3af] text-sm mb-4" data-id="element-48">
          © {new Date().getFullYear()} Código Alfa 40+. Todos los derechos
          reservados.
        </p>

        <div className="flex items-center gap-6 text-sm" data-id="element-49">
          <button onClick={() => setIsPrivacyOpen(true)} className="text-[#9ca3af] hover:text-[#c9a84c] transition-colors underline-offset-4 hover:underline" data-id="element-50">
            Política de Privacidad
          </button>
          <span className="text-gray-700" data-id="element-51">•</span>
          <button onClick={() => setIsTermsOpen(true)} className="text-[#9ca3af] hover:text-[#c9a84c] transition-colors underline-offset-4 hover:underline" data-id="element-52">
            Términos y Condiciones
          </button>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <Modal isOpen={isPrivacyOpen} onClose={() => setIsPrivacyOpen(false)} title="Política de Privacidad" data-id="element-53">
        <p data-id="element-54">
          <strong data-id="element-55">Última actualización:</strong>{' '}
          {new Date().toLocaleDateString('es-ES')}
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-56">
          1. Información que recopilamos
        </h3>
        <p data-id="element-57">
          Recopilamos información personal que usted nos proporciona
          voluntariamente al registrarse, expresar interés en obtener
          información sobre nosotros o nuestros productos, o al contactarnos. La
          información personal recopilada puede incluir: nombre, dirección de
          correo electrónico y datos de pago (procesados de forma segura a
          través de nuestros proveedores de pago).
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-58">
          2. Uso de su información
        </h3>
        <p data-id="element-59">
          Utilizamos la información recopilada para procesar sus pedidos,
          enviarle el producto digital adquirido (Código Alfa 40+), comunicarnos
          con usted sobre su compra, enviarle actualizaciones relevantes y
          mejorar nuestros servicios. No vendemos ni alquilamos su información
          personal a terceros.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-60">
          3. Cookies y tecnologías de seguimiento
        </h3>
        <p data-id="element-61">
          Podemos utilizar cookies y tecnologías de seguimiento similares para
          acceder o almacenar información. Esto nos ayuda a analizar el tráfico
          del sitio web, personalizar el contenido y mejorar su experiencia de
          usuario. Puede configurar su navegador para rechazar todas las
          cookies, pero esto puede limitar algunas funcionalidades del sitio.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-62">
          4. Seguridad de los datos
        </h3>
        <p data-id="element-63">
          Implementamos medidas de seguridad técnicas y organizativas diseñadas
          para proteger la seguridad de cualquier información personal que
          procesamos. Sin embargo, ninguna transmisión por Internet es 100%
          segura, por lo que no podemos garantizar la seguridad absoluta de sus
          datos.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-64">
          5. Retención de datos
        </h3>
        <p data-id="element-65">
          Conservaremos su información personal solo durante el tiempo que sea
          necesario para los fines establecidos en esta política, a menos que la
          ley exija o permita un período de retención más largo (como requisitos
          fiscales o contables).
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-66">
          6. Sus derechos
        </h3>
        <p data-id="element-67">
          Dependiendo de su ubicación, puede tener ciertos derechos respecto a
          su información personal, como el derecho a solicitar acceso,
          corrección o eliminación de sus datos. Para ejercer estos derechos,
          puede contactarnos a través de los canales proporcionados en el
          momento de la compra.
        </p>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={isTermsOpen} onClose={() => setIsTermsOpen(false)} title="Términos y Condiciones" data-id="element-68">
        <p data-id="element-69">
          <strong data-id="element-70">Última actualización:</strong>{' '}
          {new Date().toLocaleDateString('es-ES')}
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-71">
          1. Aceptación de los términos
        </h3>
        <p data-id="element-72">
          Al acceder y comprar el producto digital "Código Alfa 40+", usted
          acepta estar sujeto a estos Términos y Condiciones. Si no está de
          acuerdo con alguna parte de estos términos, no debe realizar la compra
          ni utilizar el producto.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-73">
          2. Descripción del producto
        </h3>
        <p data-id="element-74">
          "Código Alfa 40+" es un producto digital (Ebook y materiales
          complementarios) diseñado para proporcionar información sobre
          bienestar y estilo de vida para hombres. El contenido es puramente
          informativo y educativo. No constituye consejo médico profesional,
          diagnóstico ni tratamiento.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-75">
          3. Propiedad intelectual
        </h3>
        <p data-id="element-76">
          Todo el contenido incluido en "Código Alfa 40+", incluyendo textos,
          gráficos, logotipos y materiales descargables, es propiedad exclusiva
          de sus creadores y está protegido por las leyes internacionales de
          derechos de autor. La compra le otorga una licencia personal, no
          exclusiva e intransferible para acceder y utilizar el material. Queda
          estrictamente prohibida la reventa, distribución o modificación del
          contenido.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-77">
          4. Política de reembolso y garantía
        </h3>
        <p data-id="element-78">
          Ofrecemos una garantía de satisfacción de 7 días. Si dentro de los
          primeros 7 días posteriores a su compra usted no está satisfecho con
          el producto, puede solicitar un reembolso completo. Una vez procesado
          el reembolso, su acceso al material digital será revocado.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-79">
          5. Limitación de responsabilidad
        </h3>
        <p data-id="element-80">
          Los resultados de aplicar la información contenida en el producto
          pueden variar de persona a persona. No garantizamos resultados
          específicos. Usted asume toda la responsabilidad y riesgo por el uso
          de la información proporcionada. En ningún caso seremos responsables
          por daños directos, indirectos, incidentales o consecuentes que surjan
          del uso o la imposibilidad de usar el producto.
        </p>

        <h3 className="text-[#f5f5f5] font-semibold text-base mt-6 mb-2" data-id="element-81">
          6. Precios y pagos
        </h3>
        <p data-id="element-82">
          Los precios están sujetos a cambios sin previo aviso. El pago se
          procesa de forma segura a través de plataformas de terceros. Al
          realizar una compra, usted acepta proporcionar información de pago
          actual, completa y precisa.
        </p>
      </Modal>
    </footer>;
}